/**
 * Whole advanced pages follow the holder's own Haladó switch
 * (SPEC-TENANT-SURFACES RULE-011 WHOLE PAGES, D-TSG-23 point 3).
 *
 * A navigation-level row (nav_group, nav, page) the server resolved `advanced`
 * is a whole page of the tier. It is SHOWN - in the sidebars, as a landing
 * target and as a deploy card - only while the caller's own `advanced_mode` is
 * on, or for a caller the server resolved `unrestricted` (D-TSG-14). Like the
 * preference itself this is presentation only: the routes are still built from
 * `isUsable`, so a saved link opens the page and the API keeps serving it.
 *
 * The preference is read only when the answer holds such a row. The server
 * sends an `advanced` row only to a tier holder (or an unrestricted caller), so
 * a caller without the tier never reads it (the rule useAdvancedTier keeps).
 *
 * @module surfaces/usePagePresence
 * @see TASK-TSURF-ADVANCED-DEFAULT-BY-ROLE-CHG-004
 */
import { useCallback, useMemo } from "react";
import { useUIPreferences } from "../api/uiPreferences";
import type { SurfaceKey } from "./keys";
import type { SurfaceKind } from "./surfacesQuery";
import { useSurfaceSet } from "./useSurface";

const PAGE_KINDS: ReadonlySet<SurfaceKind> = new Set<SurfaceKind>(["nav_group", "nav", "page"]);

export interface PagePresence {
  /**
   * True while the switch is being read and the answer holds a whole advanced
   * page: a landing waits instead of deciding without it.
   */
  pending: boolean;
  /**
   * Usable AND, for a whole page resolved `advanced`, the caller unrestricted
   * or the caller's own switch on. Every other usable key - an `advanced`
   * block included, which AdvancedGroup places - is shown.
   */
  isShown: (key: SurfaceKey) => boolean;
}

/** The one predicate for what the navigation, the landing and the deploy cards show. */
export function usePagePresence(): PagePresence {
  const { surfaces, unrestricted, isUsable } = useSurfaceSet();
  const hasAdvancedPage = useMemo(
    () => Object.values(surfaces).some((row) => row.state === "advanced" && PAGE_KINDS.has(row.kind)),
    [surfaces],
  );
  const needsSwitch = hasAdvancedPage && !unrestricted;
  const preferences = useUIPreferences({ enabled: needsSwitch });
  const switchOn = preferences.status === "success" && preferences.advancedMode;

  const isShown = useCallback(
    (key: SurfaceKey): boolean => {
      if (!isUsable(key)) return false;
      const row = Object.prototype.hasOwnProperty.call(surfaces, key) ? surfaces[key] : undefined;
      if (!row || row.state !== "advanced" || !PAGE_KINDS.has(row.kind)) return true;
      return unrestricted || switchOn;
    },
    [isUsable, surfaces, unrestricted, switchOn],
  );

  const pending = needsSwitch && preferences.status === "loading";
  return useMemo(() => ({ pending, isShown }), [pending, isShown]);
}
